<script>
/**
 * This is a base component that can be used to create action buttons.
 * It will handle the loading state of the button.
 * It will pass the clickHandler method to the slot.
 * The slot will be used to define the action to be taken when the button is clicked.
 *
 * @example
 * <action-button>
 *   <template #action="{ loading, clickHandler }">
 *     <v-btn :loading="loading" @click="clickHandler(enterRoom, item)">
 *       LABEL
 *     </v-btn>
 *   </template>
 * </action-button>
 *
 * In the above example, the 'enterRoom' method will be called when the button is clicked with "item" as the parameter.
 * The button will be disabled when the 'loading' property is true.
 * The 'loading' property will be true when the button is clicked and false when the action is completed.
 *
 * @note It requires the parent component to define the action to be taken when the button is clicked.
 * @note The action SHOULD be async.
 *
 * In this way the button can be used to perform any action and the loading state will be handled automatically.
 * And the parent component will only need to define the action to be taken when the button is clicked.
 * This will make the code more readable and maintainable.
 * And the loading state will be handled automatically.
 * Also, the button can be styled as needed with any classes and any additional props, like disabled, etc.
 */
export default {
  name: "ActionButton",

  data: () => ({
    loading: false,
  }),

  methods: {
    /**
     * Handles the click event for the action button.
     *
     * @param {Function} action - The action to be performed when the button is clicked.
     * @param {...any} params - The parameters for the action function.
     */
    async clickHandler(action, ...params) {
      this.loading = true;

      await action(...params);

      this.loading = false;
    },
  },
};
</script>

<template>
  <div>
    <slot name="action" :clickHandler="clickHandler" :loading="loading"></slot>
  </div>
</template>

<style scoped lang="scss"></style>
